import React, { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Swal from "sweetalert2";
import {
  createAutomationRule,
  getAutomationById,
  updateAutomationRule,
} from "./services/AutomationServices";
import {
  IconArrowLeft,
  IconClock,
  IconCheck,
  IconX,
  IconCalendar,
} from "@tabler/icons-react";
import UserContext from "../../contexts/UserContext";
import "./AutomationRuleForm.css";
import NotificationsContext from "../../contexts/NotificationsContext";
import { useParams, useNavigate } from "react-router-dom";

// Importar contexto do AgentHub (opcional)
import { useAgentHubContext } from "../AgentHub/contexts/AgentHubContext";
import TemplateTypeSelector from "./components/TemplateTypeSelector";
import TemplateSelector from "./components/TemplateSelector";
import AutomationRuleWizard from "./components/AutomationRuleWizard";

// Schema de validação
const schema = yup
  .object({
    // template_action: yup.string().required("O tipo de mensagem é obrigatório"),
    time_value: yup
      .number()
      .required("O valor do tempo é obrigatório")
      .min(1, "Valor mínimo é 1"),
    time_unit: yup.string().required("A unidade de tempo é obrigatória"),
    direction: yup.string().required("A direção é obrigatória"),
    event: yup.string().required("O evento é obrigatório"),
    reference: yup.string().required("A referência é obrigatória"),
  })
  .required();

const AutomationRuleForm = () => {
  const { id, agentId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { setNotify } = useContext(NotificationsContext);

  // Detectar contexto do AgentHub
  const agentContext = useAgentHubContext();
  const contextAgentId = agentContext?.agentId || agentId;
  const isInsideAgentHub =
    Boolean(agentContext) || window.location.pathname.includes("/agent-hub/");

  // Estados principais
  const [loading, setLoading] = useState(false);
  const [automationData, setAutomationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);

  // Estados para templates
  const [availableTemplates, setAvailableTemplates] = useState({
    whatsapp: [],
    email: [],
    webhook: [],
  });
  const [selectedTemplateType, setSelectedTemplateType] = useState("whatsapp");
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      template_action: "confirmation_message",
      template: "",
      time_value: 1,
      time_unit: "days",
      direction: "before",
      event: "appointment",
      reference: "start",
      priority: 1,
      active: true,
    },
  });

  // Busca dados da automação e templates disponíveis
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const automationResponse = await getAutomationById(id);
        setAutomationData(automationResponse);

        // Processa templates disponíveis da API
        if (automationResponse.available_templates) {
          setAvailableTemplates({
            whatsapp: automationResponse.available_templates.whatsapp || [],
            email: automationResponse.available_templates.email || [],
            webhook: automationResponse.available_templates.webhook || [],
          });
        }

        // Para follow-up, ir direto para a configuração de timing
        if (automationResponse.automation_type === 'followup') {
          setCurrentStep(1); // Pular para timing diretamente para followup
        }

        // Se existe regra, preenche o formulário com os dados da primeira regra
        if (automationResponse.rules && automationResponse.rules.length > 0) {
          const rule = automationResponse.rules[0];

          // Para automações de follow-up, não processar templates
          if (automationResponse.automation_type !== 'followup') {
            // Identifica o tipo de template baseado na regra existente
            let templateType = "whatsapp";
            let templateFromList = null;

            // Procura o template em todos os tipos disponíveis
            for (const type of ["whatsapp", "email", "webhook"]) {
              if (automationResponse.available_templates[type]) {
                const found = automationResponse.available_templates[type].find(
                  (t) => t.id === rule.template
                );
                if (found) {
                  templateType = type;
                  templateFromList = found;
                  break;
                }
              }
            }

            setSelectedTemplateType(templateType);

            if (templateFromList) {
              setSelectedTemplate(templateFromList);
            }
          }

          // Preenche os dados no formulário
          reset({
            id: rule?.id || null,
            template_action: rule.template_action,
            template: rule.template ? rule.template.toString() : "",
            time_value: rule.time_value,
            time_unit: rule.time_unit,
            direction: rule.direction,
            event: rule.event,
            reference: rule.reference,
            priority: rule.priority || 1,
            active: rule.active !== undefined ? rule.active : true,
          });
        }
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        setNotify({
          title: "Erro",
          message: "Não foi possível carregar os dados da automação.",
          type: "error",
          visible: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, [id, reset, setNotify]);

  // Reset template quando tipo muda
  useEffect(() => {
    setValue("template", "");
    setSelectedTemplate(null);
  }, [selectedTemplateType, setValue]);

  const handleTemplateTypeSelect = (type) => {
    setSelectedTemplateType(type);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setValue("template", template.id.toString());
  };

  const onSubmit = async (data) => {
    const isFollowupType = automationData?.automation_type === 'followup';
    
    // Para automações não-followup, validar template
    if (!isFollowupType && !selectedTemplate) {
      setNotify({
        title: "Atenção!",
        message: "Selecione um template para continuar.",
        type: "warning",
        visible: true,
      });
      return;
    }

    setLoading(true);

    try {
      const formData = {
        ...data,
        user: user.user_id,
        account_id: user.account_id,
      };

      // Configurar dados de template apenas para automações não-followup
      if (!isFollowupType) {
        formData.template_id = selectedTemplate.id;
        formData.template_action = selectedTemplate.action;
        formData.template_type = selectedTemplateType;
      } else {
        // Para follow-up, usar configuração genérica
        formData.template_action = 'followup';
        formData.template_type = 'followup';
      }

      if (formData?.id) {
        await updateAutomationRule(id, formData.id, formData);
        Swal.fire({
          title: "Sucesso!",
          text: "Regra atualizada com sucesso!",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        await createAutomationRule(id, formData);
        Swal.fire({
          title: "Sucesso!",
          text: "Regra criada com sucesso!",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
      if (isInsideAgentHub) {
        navigate(`/agent-hub/${contextAgentId}/automations`);
      } else {
        navigate("/automations");
      }
    } catch (error) {
      console.error("Erro ao salvar:", error);
      Swal.fire({
        title: "Erro!",
        text: "Ocorreu um erro ao salvar a regra.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const getFilteredTemplates = () => {
    return availableTemplates[selectedTemplateType] || [];
  };

  const getTimeUnitInPortuguese = (unit) => {
    const translations = {
      minutes: "minutos",
      hours: "horas",
      days: "dias",
      weeks: "semanas",
    };
    return translations[unit] || unit;
  };

  // Funções de validação por etapa
  const validateStep1 = () => {
    return selectedTemplateType !== null;
  };

  const validateStep2 = () => {
    return selectedTemplate !== null;
  };

  const validateStep3 = async () => {
    const currentData = watch();
    try {
      await schema.validate(currentData, { abortEarly: false });
      return true;
    } catch (validationError) {
      setNotify({
        title: "Campos obrigatórios",
        message:
          "Por favor, preencha todos os campos obrigatórios antes de continuar.",
        type: "error",
        visible: true,
      });
      return false;
    }
  };

  // Função para navegar para próxima etapa
  const handleNext = async () => {
    let canProceed = true;
    const isFollowupType = automationData?.automation_type === 'followup';

    if (currentStep === 1) {
      // Para follow-up no step 1 (timing), validar campos de timing
      if (isFollowupType) {
        canProceed = await validateStep3();
      } else {
        // Para automações normais no step 1 (tipo de comunicação)
        canProceed = validateStep1();
        if (!canProceed) {
          setNotify({
            title: "Selecione um tipo",
            message: "Selecione o tipo de comunicação para continuar.",
            type: "warning",
            visible: true,
          });
        }
      }
    } else if (currentStep === 2) {
      canProceed = validateStep2();
      if (!canProceed) {
        setNotify({
          title: "Selecione um template",
          message: "Selecione um template para continuar.",
          type: "warning",
          visible: true,
        });
      }
    } else if (currentStep === 3) {
      // Para automações normais no step 3 (timing)
      canProceed = await validateStep3();
    }

    return canProceed;
  };

  // Função para renderizar o conteúdo de cada etapa
  const renderStepContent = () => {
    const isFollowupType = automationData?.automation_type === 'followup';
    
    switch (currentStep) {
      case 1:
        // Para followup, mostrar diretamente a configuração de timing
        if (isFollowupType) {
          return (
            <div>
              <div className="automation-rule-form__step-header">
                <div className="automation-rule-form__step-number">1</div>
                <h3 className="automation-rule-form__step-title">
                  Configure o Timing do Follow-up
                </h3>
              </div>

              <form
                className="automation-rule-form__form"
                onSubmit={handleSubmit(onSubmit)}
              >
                {/* Event Configuration */}
                <div className="automation-rule-form__form-row">
                  <div className="automation-rule-form__form-group">
                    <label className="automation-rule-form__label">
                      Evento
                    </label>
                    <select
                      className={`automation-rule-form__select ${
                        errors.event
                          ? "automation-rule-form__select--error"
                          : ""
                      }`}
                      {...register("event")}
                    >
                      <option value="appointment">Agendamento</option>
                      <option value="data_collection">Coleta de dados</option>
                    </select>
                    {errors.event && (
                      <span className="automation-rule-form__error-text">
                        {errors.event.message}
                      </span>
                    )}
                  </div>
                </div>

                {/* Timing Configuration */}
                <div className="automation-rule-form__timing-section">
                  <div className="automation-rule-form__timing-header">
                    <IconClock size={20} />
                    <h4 className="automation-rule-form__timing-title">
                      Configuração de Timing
                    </h4>
                  </div>

                  <div className="automation-rule-form__timing-row">
                    <div className="automation-rule-form__timing-group">
                      <label className="automation-rule-form__label">
                        Tempo
                      </label>
                      <input
                        type="number"
                        min="1"
                        className={`automation-rule-form__input ${
                          errors.time_value
                            ? "automation-rule-form__input--error"
                            : ""
                        }`}
                        {...register("time_value")}
                      />
                      {errors.time_value && (
                        <span className="automation-rule-form__error-text">
                          {errors.time_value.message}
                        </span>
                      )}
                    </div>

                    <div className="automation-rule-form__timing-group">
                      <label className="automation-rule-form__label">
                        Unidade
                      </label>
                      <select
                        className={`automation-rule-form__select ${
                          errors.time_unit
                            ? "automation-rule-form__select--error"
                            : ""
                        }`}
                        {...register("time_unit")}
                      >
                        <option value="minutes">Minutos</option>
                        <option value="hours">Horas</option>
                        <option value="days">Dias</option>
                        <option value="weeks">Semanas</option>
                      </select>
                      {errors.time_unit && (
                        <span className="automation-rule-form__error-text">
                          {errors.time_unit.message}
                        </span>
                      )}
                    </div>
                    <div className="automation-rule-form__timing-group">
                      <label className="automation-rule-form__label">
                        Enviar
                      </label>
                      <select
                        className={`automation-rule-form__select ${
                          errors.direction
                            ? "automation-rule-form__select--error"
                            : ""
                        }`}
                        {...register("direction")}
                      >
                        {watch("event") !== "data_collection" && (
                          <option value="before">Antes</option>
                        )}
                        <option value="after">Depois</option>
                      </select>
                      {errors.direction && (
                        <span className="automation-rule-form__error-text">
                          {errors.direction.message}
                        </span>
                      )}
                    </div>
                    <div className="automation-rule-form__timing-group">
                      <label className="automation-rule-form__label">
                        Referência
                      </label>
                      <select
                        className={`automation-rule-form__select ${
                          errors.reference
                            ? "automation-rule-form__select--error"
                            : ""
                        }`}
                        {...register("reference")}
                      >
                        {watch("event") !== "data_collection" && (
                          <option value="start">Início</option>
                        )}
                        <option value="created_at">Criação</option>
                      </select>
                      {errors.reference && (
                        <span className="automation-rule-form__error-text">
                          {errors.reference.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="automation-rule-form__timing-example">
                    <IconCalendar size={16} />
                    <span className="automation-rule-form__example-text">
                      Exemplo: Enviar{" "}
                      {watch("direction") === "before" ? "before" : "after"} do{" "}
                      {watch("event") === "appointment"
                        ? "agendamento"
                        : "evento"}{" "}
                      {watch("time_value")}{" "}
                      {getTimeUnitInPortuguese(watch("time_unit"))} baseado na{" "}
                      {watch("reference") === "start"
                        ? "data de início"
                        : "data de criação"}
                    </span>
                  </div>
                </div>

                {/* Active Toggle */}
                <div className="automation-rule-form__form-group">
                  <div className="automation-rule-form__checkbox-container">
                    <input
                      type="checkbox"
                      id="active-toggle"
                      className="automation-rule-form__checkbox"
                      {...register("active")}
                    />
                    <label
                      htmlFor="active-toggle"
                      className="automation-rule-form__checkbox-label"
                    >
                      Regra Ativa
                    </label>
                  </div>
                  <span className="automation-rule-form__help-text">
                    Se desativada, esta regra específica não gerará mensagens.
                  </span>
                </div>
              </form>
            </div>
          );
        }
        
        // Para outros tipos, mostrar seleção de tipo de comunicação
        return (
          <div>
            <div className="automation-rule-form__step-header">
              <div className="automation-rule-form__step-number">1</div>
              <h3 className="automation-rule-form__step-title">
                Selecione o Tipo de Comunicação
              </h3>
            </div>

            <TemplateTypeSelector
              selectedType={selectedTemplateType}
              onTypeSelect={handleTemplateTypeSelect}
              availableTemplates={availableTemplates}
            />
          </div>
        );

      case 2:
        return (
          <div>
            <div className="automation-rule-form__step-header">
              <div className="automation-rule-form__step-number">2</div>
              <h3 className="automation-rule-form__step-title">
                Escolha o Template
              </h3>
            </div>

            {selectedTemplateType && getFilteredTemplates().length > 0 ? (
              <TemplateSelector
                templateType={selectedTemplateType}
                availableTemplates={getFilteredTemplates()}
                selectedTemplate={selectedTemplate}
                onTemplateSelect={handleTemplateSelect}
                loading={false}
              />
            ) : (
              <div
                style={{ textAlign: "center", padding: "40px", color: "#666" }}
              >
                {!selectedTemplateType
                  ? "Primeiro selecione o tipo de comunicação na etapa anterior."
                  : "Nenhum template disponível para o tipo selecionado."}
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div>
            <div className="automation-rule-form__step-header">
              <div className="automation-rule-form__step-number">3</div>
              <h3 className="automation-rule-form__step-title">
                Configure o Timing
              </h3>
            </div>

            {selectedTemplate ? (
              <form
                className="automation-rule-form__form"
                onSubmit={handleSubmit(onSubmit)}
              >
                {/* Message Type and Event */}
                <div className="automation-rule-form__form-row">
                 {/*  <div className="automation-rule-form__form-group">
                    <label className="automation-rule-form__label">
                      Tipo de Mensagem
                    </label>
                    <select
                      className={`automation-rule-form__select ${
                        errors.template_action
                          ? "automation-rule-form__select--error"
                          : ""
                      }`}
                      {...register("template_action")}
                    >
                      <option value="confirmation_message">Confirmação</option>
                      <option value="reminder_message">Lembrete</option>
                      <option value="feedback">Feedback</option>
                      <option value="tips_message">Dicas</option>
                      <option value="generic">Genérico</option>
                    </select>
                    {errors.template_action && (
                      <span className="automation-rule-form__error-text">
                        {errors.template_action.message}
                      </span>
                    )}
                  </div> */}

                  <div className="automation-rule-form__form-group">
                    <label className="automation-rule-form__label">
                      Evento
                    </label>
                    <select
                      className={`automation-rule-form__select ${
                        errors.event
                          ? "automation-rule-form__select--error"
                          : ""
                      }`}
                      {...register("event")}
                    >
                      <option value="appointment">Agendamento</option>
                      <option value="data_collection">Coleta de dados</option>
                    </select>
                    {errors.event && (
                      <span className="automation-rule-form__error-text">
                        {errors.event.message}
                      </span>
                    )}
                  </div>
                </div>

                {/* Timing Configuration */}
                <div className="automation-rule-form__timing-section">
                  <div className="automation-rule-form__timing-header">
                    <IconClock size={20} />
                    <h4 className="automation-rule-form__timing-title">
                      Configuração de Timing
                    </h4>
                  </div>

                  <div className="automation-rule-form__timing-row">
                    <div className="automation-rule-form__timing-group">
                      <label className="automation-rule-form__label">
                        Tempo
                      </label>
                      <input
                        type="number"
                        min="1"
                        className={`automation-rule-form__input ${
                          errors.time_value
                            ? "automation-rule-form__input--error"
                            : ""
                        }`}
                        {...register("time_value")}
                      />
                      {errors.time_value && (
                        <span className="automation-rule-form__error-text">
                          {errors.time_value.message}
                        </span>
                      )}
                    </div>

                    <div className="automation-rule-form__timing-group">
                      <label className="automation-rule-form__label">
                        Unidade
                      </label>
                      <select
                        className={`automation-rule-form__select ${
                          errors.time_unit
                            ? "automation-rule-form__select--error"
                            : ""
                        }`}
                        {...register("time_unit")}
                      >
                        <option value="minutes">Minutos</option>
                        <option value="hours">Horas</option>
                        <option value="days">Dias</option>
                        <option value="weeks">Semanas</option>
                      </select>
                      {errors.time_unit && (
                        <span className="automation-rule-form__error-text">
                          {errors.time_unit.message}
                        </span>
                      )}
                    </div>
                    <div className="automation-rule-form__timing-group">
                      <label className="automation-rule-form__label">
                        Enviar
                      </label>
                      <select
                        className={`automation-rule-form__select ${
                          errors.direction
                            ? "automation-rule-form__select--error"
                            : ""
                        }`}
                        {...register("direction")}
                      >
                        {watch("event") !== "data_collection" && (
                          <option value="before">Antes</option>
                        )}
                        <option value="after">Depois</option>
                      </select>
                      {errors.direction && (
                        <span className="automation-rule-form__error-text">
                          {errors.direction.message}
                        </span>
                      )}
                    </div>
                    <div className="automation-rule-form__timing-group">
                      <label className="automation-rule-form__label">
                        Referência
                      </label>
                      <select
                        className={`automation-rule-form__select ${
                          errors.reference
                            ? "automation-rule-form__select--error"
                            : ""
                        }`}
                        {...register("reference")}
                      >
                        {watch("event") !== "data_collection" && (
                          <option value="start">Início</option>
                        )}
                        <option value="created_at">Criação</option>
                      </select>
                      {errors.reference && (
                        <span className="automation-rule-form__error-text">
                          {errors.reference.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="automation-rule-form__timing-example">
                    <IconCalendar size={16} />
                    <span className="automation-rule-form__example-text">
                      Exemplo: Enviar{" "}
                      {watch("direction") === "before" ? "before" : "after"} do{" "}
                      {watch("event") === "appointment"
                        ? "agendamento"
                        : "evento"}{" "}
                      {watch("time_value")}{" "}
                      {getTimeUnitInPortuguese(watch("time_unit"))} baseado na{" "}
                      {watch("reference") === "start"
                        ? "data de início"
                        : "data de criação"}
                    </span>
                  </div>
                </div>

                {/* Active Toggle */}
                <div className="automation-rule-form__form-group">
                  <div className="automation-rule-form__checkbox-container">
                    <input
                      type="checkbox"
                      id="active-toggle"
                      className="automation-rule-form__checkbox"
                      {...register("active")}
                    />
                    <label
                      htmlFor="active-toggle"
                      className="automation-rule-form__checkbox-label"
                    >
                      Regra Ativa
                    </label>
                  </div>
                  <span className="automation-rule-form__help-text">
                    Se desativada, esta regra específica não gerará mensagens.
                  </span>
                </div>
              </form>
            ) : (
              <div
                style={{ textAlign: "center", padding: "40px", color: "#666" }}
              >
                Primeiro selecione um template na etapa anterior para configurar
                o timing.
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="automation-rule-form">
        <div className="automation-rule-form__loading-container">
          <div className="automation-rule-form__loading-spinner"></div>
          <p className="automation-rule-form__loading-text">
            Carregando dados da automação...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="automation-rule-form">
      {/* Header com botão de voltar */}
      <div className="automation-rule-form__header">
        <div className="automation-rule-form__header-content">
          <button
            className="automation-rule-form__back-button"
            onClick={() => {
              if (isInsideAgentHub) {
                navigate(`/agent-hub/${contextAgentId}/automations`);
              } else {
                navigate("/automations");
              }
            }}
          >
            <IconArrowLeft size={20} />
          </button>
          {automationData && (
            <div
              className={`automation-rule-form__status-badge ${
                automationData.active
                  ? "automation-rule-form__status-badge--active"
                  : "automation-rule-form__status-badge--inactive"
              }`}
            >
              {automationData.active ? (
                <>
                  <IconCheck size={16} />
                  Ativo
                </>
              ) : (
                <>
                  <IconX size={16} />
                  Inativo
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Wizard Container */}
      <AutomationRuleWizard
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        onNext={handleNext}
        onSubmit={handleSubmit(onSubmit)}
        loading={loading}
        isEditing={!!id}
        includeAutomationStep={false}
        automationType={automationData?.automation_type}
      >
        {renderStepContent()}
      </AutomationRuleWizard>
    </div>
  );
};

export default AutomationRuleForm;
